import React, { useEffect, useRef, useState } from "react";
import BimatterViewer from "bimatter-viewer";
import config from "../../config";
import {
    initViewer,
    setModel,
} from "../../app/features/viewer/viewerMainSlice";
import {
    useAppDispatch,
    useAppSelector,
    useViewer,
} from "../../app/hooks/hooks";
import { keyEv } from "./ViewerKeyBoardEvents/ViewerKeyBoardEvents";
import ViewerButtons from "./ViewerButtons/ViewerButtons";
import { useGetStructureMutation } from "../../app/services/params/params";
import {
    selectViewerPropsVisibility,
    selectViewerTreeVisibility,
    toggleViewerTreeVisibility,
} from "../../app/features/viewer/buttonsSlice/buttonsSlice";
import ViewerProps from "./ViewerProps/ViewerProps";
import classes from "./Viewer.module.css";
import ViewerTree, { Chunk } from "./ViewerTree/ViewerTree";
import ViewerContextMenu from "./ViewerContextMenu/ViewerContextMenu";
import BimatterLoader from "../Loaders/BimatterLoader";

interface ViewerProps {
    model_paths?: string[];
    files?: FileList;
}
function DemoViewer({ model_paths, files }: ViewerProps) {
    const dispatch = useAppDispatch();
    const viewerTreeVisibility = useAppSelector(selectViewerTreeVisibility);
    const viewerPropsVisibility = useAppSelector(selectViewerPropsVisibility);
    const ref = useRef(null);
    const [curTree, setCurTree] = useState<{ [modelID: number]: Chunk } | null>(
        null
    );
    const [viewerOnFocuse, setViewerOnFocuse] = useState(true);
    const [loading, setLoading] = useState(true);
    const [getStructure] = useGetStructureMutation();

    useEffect(() => {
        const viewer = new BimatterViewer({
            container: ref.current!,
            useDefaultTexture: false,
        });
        viewer.context.renderer.threeRenderer.setClearColor(0x000000, 0);
        viewer.utils.navigationCubeUtil.active = true;
        viewer.utils.propsUtils.useDefaultGetPropertiesById = false;
        // const model_path = "ar.min.bmt";

        const nouseOn = () => {
            setViewerOnFocuse(true);
        };
        const nouseOut = () => {
            setViewerOnFocuse(false);
        };
        const curKeyEv = (e: KeyboardEvent) => keyEv(e, viewer);
        document.addEventListener("keydown", curKeyEv);
        viewer.container.addEventListener("mouseenter", nouseOn);
        viewer.container.addEventListener("mouseleave", nouseOut);
        loadModels(viewer, model_paths ? model_paths : Array.from(files!));
        // if (model_paths) {
        //     dispatch(toggleViewerTreeVisibility());
        // } else if (files) {
        //     for (const file of Array.from(files)) {
        //         viewer.loadModel(file, true).then((model) => {
        //             setLoading(false);
        //             dispatch(
        //                 setModel({
        //                     modelID: model.modelID,
        //                     model_id: file.name,
        //                 })
        //             );
        //             const structure = viewer.models[model.modelID].properties
        //                 .structure as Chunk;
        //             setCurTree((ex) => {
        //                 if (!ex) {
        //                     return { [model.modelID]: structure };
        //                 }
        //                 return {
        //                     ...ex,
        //                     [model.modelID]: structure,
        //                 };
        //             });
        //         });
        //     }
        //     dispatch(toggleViewerTreeVisibility());
        // }
        //@ts-ignore
        window.viewer = viewer;
        dispatch(
            initViewer(() => {
                return viewer;
            })
        );
        return () => {
            document.removeEventListener("keydown", curKeyEv);
            if (viewer) {
                viewer.container.removeEventListener("mouseenter", nouseOn);
                viewer.container.removeEventListener("mouseleave", nouseOut);
                // viewer.dispose()
                console.log("need to dispose viewer");
            }
        };
    }, []);

    const loadModels = async (
        viewer: BimatterViewer,
        argArr: string[] | File[]
    ) => {
        const models: number[] = [];
        for (const curArg of argArr) {
            if (curArg instanceof File) {
                if (!models.length) {
                    await loadModelByFile(
                        viewer,
                        curArg,
                        models,
                        argArr.length
                    );
                } else {
                    loadModelByFile(viewer, curArg, models, argArr.length);
                }
            } else {
                if (!models.length) {
                    await loadModelByPath(
                        viewer,
                        curArg,
                        models,
                        argArr.length
                    );
                } else {
                    loadModelByPath(viewer, curArg, models, argArr.length);
                }
            }
        }
    };
    const loadModelByFile = async (
        viewer: BimatterViewer,
        file: File,
        models: any[],
        modelsCount: number
    ) => {
        const model = await viewer.loadModel(file, true);
        if (!model) return;
        models.push(model.modelID);
        if (models.length === modelsCount) {
            setLoading(false);
        }
        dispatch(
            setModel({
                modelID: model.modelID,
                model_id: file.name,
            })
        );
        const structure = viewer.models[model.modelID].properties
            .structure as Chunk;
        setCurTree((ex) => {
            if (!ex) {
                return { [model.modelID]: structure };
            }
            return {
                ...ex,
                [model.modelID]: structure,
            };
        });
    };
    const loadModelByPath = async (
        viewer: BimatterViewer,
        path: string,
        models: any[],
        modelsCount: number
    ) => {
        const model = await viewer.loadModel(`${config.SERVER}/${path}`, true);
        if (!model) return;
        models.push(model.modelID);

        if (models.length === modelsCount) {
            setLoading(false);
        }
        dispatch(
            setModel({
                modelID: model.modelID,
                model_id: path,
            })
        );
        getStructure(model.modelID)
            .unwrap()
            .then((structure) => {
                model.properties.setStructure(structure);
                setCurTree((ex) => {
                    if (!ex) {
                        return {
                            [model.modelID]: structure,
                        };
                    }
                    return {
                        ...ex,
                        [model.modelID]: structure,
                    };
                });
            });
    };
    return (
        <ViewerContextMenu enable={viewerOnFocuse}>
            <BimatterLoader loading={loading}></BimatterLoader>
            <ViewerButtons></ViewerButtons>
            {viewerTreeVisibility ? (
                curTree ? (
                    <ViewerTree tree={structuredClone(curTree)} />
                ) : (
                    <></>
                )
            ) : (
                <></>
            )}
            <div className={classes.viewerBackground}></div>
            <div ref={ref} className={classes.viewerContainer} />

            {viewerPropsVisibility ? <ViewerProps></ViewerProps> : <></>}
        </ViewerContextMenu>
    );
}

export default DemoViewer;
